import React from "react";
import { motion } from "framer-motion";
import { useLang, pick } from "../context/LanguageContext";
import { t } from "../data/translations";
import { MapPin, Clock, Phone, Mail } from "lucide-react";

export const Visit = ({ settings }) => {
  const { lang } = useLang();
  const mapUrl = `https://www.google.com/maps/dir/?api=1&destination=${settings?.map_lat},${settings?.map_lng}`;
  const embedUrl = `https://maps.google.com/maps?q=${settings?.map_lat},${settings?.map_lng}&z=15&output=embed`;

  const rows = [
    { icon: MapPin, label: t.visit.address, value: settings?.address },
    { icon: Clock, label: t.visit.hours, value: pick(settings?.hours, lang) },
    settings?.phone ? { icon: Phone, label: t.visit.phone, value: settings.phone } : null,
    settings?.email ? { icon: Mail, label: { en: "Email", es: "Correo" }, value: settings.email } : null,
  ].filter(Boolean);

  return (
    <section id="visit" className="relative max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-32" data-testid="visit-section">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <p className="text-brand-gold uppercase tracking-[0.3em] text-xs mb-4">{pick(t.visit.kicker, lang)}</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-10 text-balance">
            {pick(t.visit.title, lang)}
          </h2>

          <div className="space-y-7">
            {rows.map((r, i) => (
              <div key={i} className="flex items-start gap-4" data-testid={`visit-row-${i}`}>
                <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-primary/15 text-brand-primary">
                  <r.icon size={18} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-brand-muted mb-1">{pick(r.label, lang)}</p>
                  <p className="text-lg text-brand-text">{r.value}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-4">
            <a href={mapUrl} target="_blank" rel="noreferrer" data-testid="directions-btn" className="rounded-full bg-brand-primary hover:bg-brand-primaryHover px-7 py-3.5 font-semibold transition-colors duration-300">
              {pick(t.visit.directions, lang)}
            </a>
            <a href={settings?.doordash_url || "#"} target="_blank" rel="noreferrer" className="rounded-full border border-white/25 hover:border-brand-gold px-7 py-3.5 font-semibold transition-colors duration-300">
              {pick(t.hero.order, lang)}
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative rounded-[2rem] overflow-hidden ring-1 ring-white/10 h-[420px] lg:h-[520px]"
        >
          <iframe
            title="map"
            src={embedUrl}
            className="w-full h-full grayscale-[0.3] contrast-125"
            style={{ border: 0, filter: "invert(0.9) hue-rotate(180deg)" }}
            loading="lazy"
          />
        </motion.div>
      </div>
    </section>
  );
};
